import React, { useState, FC, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TwSelect, { TwSelectOption } from '../TwSelects/TwSelects';
import TwField, { TwInputTypes } from '../TwFields/TwFields';
import CircularProgress from '@mui/material/CircularProgress';
import { ReactComponent as Send } from '../../assets/icons/send-note.svg';
import { ReactComponent as DispatchOnly } from '../../assets/icons/dispatch-only-lock.svg';
import { ReactComponent as DispatchAndDrivers } from '../../assets/icons/driver-wheel.svg';
import { CreateDispatchNote, DispatchNoteType } from './TwDispatchNotes';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import useAuth from '../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../providers/User/User.model';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import { usePostDispatchNoteMutation } from '../../redux/api/orders/ordersApi';

interface TwDispatchNotesTextEditorProps {
  orderId?: string;
  tripId?: string;
  isDriverLink?: boolean;
  token?: string;
  refresh: () => void;
}

const TwDispatchNotesTextEditor: FC<TwDispatchNotesTextEditorProps> = ({
  orderId,
  tripId,
  isDriverLink,
  token,
  refresh
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const [noteType, setNoteType] = useState<TwSelectOption>({
    label: 'Dispatch & drivers',
    value: DispatchNoteType.DISPATCH_CARRIER_AND_DRIVERS,
    icon: <DispatchAndDrivers />
  });
  const [note, setNote] = useState<string>('');
  const [enableBlueIcon, setEnableBlueIcon] = useState<boolean>(false);
  const [disableSelect, setDisableSelect] = useState<boolean>(false);
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
  const { addNotification } = useNotifications();
  const isDriver = userData?.role === USER_ROLE.DRIVER || isDriverLink;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const [postDispatchNote] = usePostDispatchNoteMutation();

  const isDriverOrCarrier = isDriver || isCarrier;

  const selectDispatchOptions: TwSelectOption[] = [
    {
      label: 'Dispatch only',
      value: DispatchNoteType.DISPATCH_ONLY,
      icon: <DispatchOnly />
    },
    {
      label: 'Dispatch & drivers',
      value: DispatchNoteType.DISPATCH_CARRIER_AND_DRIVERS,
      icon: <DispatchAndDrivers />
    }
  ];

  const addNote = async (): Promise<void> => {
    if (note === '') {
      addNotification({
        message: 'Please write a note before sending',
        variant: 'info'
      });
      return;
    }

    const body: CreateDispatchNote = {
      note: note,
      note_type: noteType.value as DispatchNoteType
    };

    if (orderId) {
      body.orderId = orderId;
    }

    if (tripId) {
      body.tripId = tripId;
    }

    if (isDriverOrCarrier) {
      body.note_type = DispatchNoteType.DISPATCH_CARRIER_AND_DRIVERS;
    }

    if (token) {
      body.token = token;
    }

    try {
      setIsApiLoading(true);
      postDispatchNote(body)
        .unwrap()
        .then((res) => {
          addNotification({
            message: 'Note added successfully',
            variant: 'success'
          });

          setEnableBlueIcon(false);
          setNote('');
          refresh();
        })
        .catch((error) => {
          addNotification({
            message: error.data.message || 'Error adding note, please try again',
            variant: 'error'
          });
        })
        .finally(() => {
          setIsApiLoading(false);
        });
    } catch (error: any) {
      setIsApiLoading(false);
      addNotification({
        message: error.data.message || 'Error adding note, please try again',
        variant: 'error'
      });
    }
  };

  useEffect(() => {
    if (orderId && enableLtl && !tripId) {
      setDisableSelect(true);
      setNoteType({
        label: 'Dispatch only',
        value: DispatchNoteType.DISPATCH_ONLY,
        icon: <DispatchOnly />
      });
    }
  }, [orderId]);

  return (
    <Box className="dispatch-notes__texteditor-container">
      <TwField
        name="dispatch-notes"
        placeholder="Write a note..."
        inputType={TwInputTypes.textarea}
        className={
          isDriverOrCarrier
            ? 'dispatch-notes__texteditor-field'
            : 'dispatch-notes__texteditor-field-with-actions'
        }
        onInputChange={(e: any): void => {
          setNote(e.target.value);

          if (e.target.value !== '') {
            setEnableBlueIcon(true);
          } else {
            setEnableBlueIcon(false);
          }
        }}
        value={note}
        maxRows={1}
      />
      <Stack
        flexDirection={'row'}
        justifyContent={'flex-end'}
        className="dispatch-notes__texteditor-actions"
        sx={{
          width: !matchesMobileOrTablet ? 'inherit' : '275px'
        }}
      >
        {!isDriverOrCarrier && (
          <TwSelect
            name="dispatch-notes-type"
            optionList={selectDispatchOptions}
            value={noteType.value}
            onInputChange={(e: any): void => {
              const selectedOption = selectDispatchOptions.find(
                (option) => option.value === e.target.value
              );
              if (selectedOption) {
                setNoteType(selectedOption);
              }
            }}
            sx={{
              width: !matchesMobileOrTablet ? '185px' : '55px',
              height: '30px'
            }}
            showCheckmark={true}
            renderValue={(value: string): React.ReactNode => {
              const selectedOption = selectDispatchOptions.find((option) => option.value === value);

              if (!selectedOption) {
                return null;
              }

              return (
                <Stack direction="row" alignItems="center" className="tw-selects__text-container">
                  {selectedOption.icon && selectedOption.icon}
                  {!matchesMobileOrTablet ? selectedOption.label : null}
                </Stack>
              );
            }}
            disabled={disableSelect}
          />
        )}
        <Box className="dispatch-notes__texteditor-separator"></Box>
        <IconButton
          onClick={addNote}
          className="dispatch-notes__texteditor-send"
          disabled={isApiLoading}
        >
          {isApiLoading && <CircularProgress size={20} />}
          {!isApiLoading && (
            <Send className={enableBlueIcon ? 'dispatch-notes__texteditor-send__blue' : ''} />
          )}
        </IconButton>
      </Stack>
    </Box>
  );
};

export default TwDispatchNotesTextEditor;
